/**
 * BathymetryMap.tsx — Interactive bathymetric map renderer with click-to-place
 *                     aeration module positioning.
 *
 *   - Renders depth raster as a coloured grid (shallow = light cyan, deep = navy)
 *   - Overlays basin outline polygon
 *   - Marks deepest point and deep-zone (deepest 30 %) cells
 *   - Click anywhere INSIDE the polygon to place a module
 *   - Drag a module to reposition; right-click to delete
 *   - Shows depth tooltip on hover
 *   - Reports placed positions in real metres relative to the basin's local origin
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Trash2, Plus, Grid3X3, RotateCcw, Save } from 'lucide-react';
import type { SiteBathymetry } from '@/lib/benchmarkSites';
import { buildDepthRaster, depthToColour, meanDepth } from '@/lib/bathymetryField';

export interface PlacedModule {
  id: string;
  /** Relative coordinates 0-1 within the basin extent. */
  x: number;
  y: number;
  /** Depth in metres at this point. */
  depth: number;
}

interface Props {
  bathymetry: SiteBathymetry;
  /** Max depth in metres (from BenchmarkInputs.depth_max_m). */
  depthMax: number;
  /** Site name for the legend. */
  siteName?: string;
  /** Initial placed modules. */
  initialModules?: PlacedModule[];
  /** Called whenever modules are placed/moved/removed. */
  onChange?: (modules: PlacedModule[]) => void;
  /** Width in CSS pixels. */
  width?: number;
}

export default function BathymetryMap({
  bathymetry, depthMax, siteName,
  initialModules = [], onChange, width = 720,
}: Props) {
  const raster = useMemo(() => buildDepthRaster(bathymetry, depthMax), [bathymetry, depthMax]);
  const aspect = bathymetry.extent_m.height / bathymetry.extent_m.width;
  const height = Math.max(280, Math.round(width * aspect));

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [modules, setModules] = useState<PlacedModule[]>(initialModules);
  const [hoverDepth, setHoverDepth] = useState<number | null>(null);
  const [hoverPos, setHoverPos] = useState<{ x: number; y: number } | null>(null);
  const [showGrid, setShowGrid] = useState(false);
  const [showDeepZone, setShowDeepZone] = useState(true);

  // Notify parent on change
  useEffect(() => { onChange?.(modules); }, [modules, onChange]);

  // Re-sync from parent when initialModules content (NOT just reference) changes.
  // Comparing by stable signature avoids the infinite loop that occurs when the
  // parent rebuilds the initialModules array each render.
  const initialSignature = initialModules
    .map(m => `${m.id}|${m.x.toFixed(4)}|${m.y.toFixed(4)}|${m.depth.toFixed(2)}`)
    .join(';');
  useEffect(() => {
    setModules(initialModules);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSignature]);

  // ── Drawing ────────────────────────────────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    // Background (land)
    ctx.fillStyle = '#0a0f1a';
    ctx.fillRect(0, 0, width, height);

    // Depth grid
    const N = raster.N;
    const cw = width / N;
    const ch = height / N;
    for (let j = 0; j < N; j++) {
      for (let i = 0; i < N; i++) {
        const d = raster.depths[j][i];
        if (!isFinite(d)) continue;
        // Deep-zone tint
        const isDeep = showDeepZone && d >= raster.deepThreshold;
        ctx.fillStyle = depthToColour(d, depthMax);
        ctx.fillRect(i * cw, j * ch, cw + 0.5, ch + 0.5);
        if (isDeep) {
          ctx.fillStyle = 'rgba(255, 215, 0, 0.15)';   // gold tint on deep-zone
          ctx.fillRect(i * cw, j * ch, cw + 0.5, ch + 0.5);
        }
      }
    }

    // Grid lines
    if (showGrid) {
      ctx.strokeStyle = 'rgba(255,255,255,0.06)';
      ctx.lineWidth = 1;
      for (let i = 1; i < N; i++) {
        const x = i * cw;
        ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, height); ctx.stroke();
        const y = i * ch;
        ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(width, y); ctx.stroke();
      }
    }

    // Polygon outline
    ctx.strokeStyle = '#7dd3fc';
    ctx.lineWidth = 2;
    ctx.beginPath();
    bathymetry.outline.forEach(([x, y], k) => {
      const px = x * width, py = y * height;
      if (k === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
    });
    ctx.closePath();
    ctx.stroke();

    // Deepest-point cross
    const [dx, dy] = bathymetry.deepest;
    const cx = dx * width, cy = dy * height;
    ctx.strokeStyle = '#ffd700';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(cx - 7, cy); ctx.lineTo(cx + 7, cy);
    ctx.moveTo(cx, cy - 7); ctx.lineTo(cx, cy + 7);
    ctx.stroke();

    // Placed modules
    modules.forEach((m, idx) => {
      const mx = m.x * width, my = m.y * height;
      // plume circle (visualise notional coverage)
      ctx.fillStyle = 'rgba(16, 185, 129, 0.18)';
      ctx.beginPath(); ctx.arc(mx, my, 22, 0, Math.PI * 2); ctx.fill();
      // marker
      ctx.fillStyle = '#10b981';
      ctx.beginPath(); ctx.arc(mx, my, 5, 0, Math.PI * 2); ctx.fill();
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 1.5;
      ctx.stroke();
      // index label
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 10px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(String(idx + 1), mx, my);
    });

    // Scale bar
    const barM = 500;
    const barPx = (barM / bathymetry.extent_m.width) * width;
    ctx.fillStyle = '#fff';
    ctx.fillRect(10, height - 18, barPx, 3);
    ctx.font = '10px monospace';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.fillText(`${barM} m`, 10, height - 13);
  }, [raster, bathymetry, depthMax, modules, showGrid, showDeepZone, width, height]);

  // ── Click / hover handlers ─────────────────────────────────────
  const eventToRelative = (e: React.MouseEvent) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return null;
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    return { x, y };
  };

  const cellDepthAt = (x: number, y: number): number | null => {
    const N = raster.N;
    const i = Math.floor(x * N);
    const j = Math.floor(y * N);
    if (i < 0 || j < 0 || i >= N || j >= N) return null;
    const d = raster.depths[j]?.[i];
    return isFinite(d) ? d : null;
  };

  const handleClick = (e: React.MouseEvent) => {
    const rel = eventToRelative(e);
    if (!rel) return;
    const depth = cellDepthAt(rel.x, rel.y);
    if (depth === null) return;       // clicked on land
    setModules(prev => [
      ...prev,
      { id: `m_${Date.now()}_${prev.length}`, x: rel.x, y: rel.y, depth },
    ]);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    const rel = eventToRelative(e);
    if (!rel) return;
    const d = cellDepthAt(rel.x, rel.y);
    setHoverDepth(d);
    setHoverPos(d !== null ? { x: e.clientX, y: e.clientY } : null);
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    const rel = eventToRelative(e);
    if (!rel) return;
    // delete nearest module within 25 px
    const N = raster.N;
    let nearest = -1; let nearestDist = Infinity;
    modules.forEach((m, i) => {
      const dpx = Math.hypot((m.x - rel.x) * width, (m.y - rel.y) * height);
      if (dpx < 25 && dpx < nearestDist) { nearest = i; nearestDist = dpx; }
    });
    if (nearest >= 0) setModules(prev => prev.filter((_, i) => i !== nearest));
    // suppress unused N warning
    void N;
  };

  const clearAll = () => setModules([]);

  const autoGrid = () => {
    // Generate a hex grid of points within the polygon at ~ 1.5× plume radius spacing
    const newMods: PlacedModule[] = [];
    const N = raster.N;
    const step = Math.max(3, Math.floor(N / 8));      // 8 modules across major axis
    for (let j = step / 2; j < N; j += step) {
      const rowOffset = ((Math.floor(j / step)) % 2) * step / 2;
      for (let i = step / 2 + rowOffset; i < N; i += step) {
        const x = i / N, y = j / N;
        const d = cellDepthAt(x, y);
        if (d !== null) {
          newMods.push({ id: `auto_${i}_${j}`, x, y, depth: d });
        }
      }
    }
    setModules(newMods);
  };

  const exportJSON = () => {
    const blob = new Blob([JSON.stringify({
      site: siteName, modules,
      meanDepth_m: meanDepth(raster).toFixed(2),
      timestamp: new Date().toISOString(),
    }, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `module-layout-${(siteName ?? 'site').replace(/\s+/g, '-')}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const meanD = useMemo(() => meanDepth(raster), [raster]);

  return (
    <div className="space-y-2">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-xs text-slate-400 flex items-center gap-2 flex-wrap">
          <span>Click to place · Right-click to delete · Hover for depth</span>
          <label className="inline-flex items-center gap-1">
            <input type="checkbox" checked={showGrid} onChange={e => setShowGrid(e.target.checked)} /> Grid
          </label>
          <label className="inline-flex items-center gap-1">
            <input type="checkbox" checked={showDeepZone} onChange={e => setShowDeepZone(e.target.checked)} /> Deep-zone tint
          </label>
        </div>
        <div className="flex items-center gap-1.5">
          <button onClick={autoGrid} className="inline-flex items-center gap-1 rounded-md border border-slate-700 bg-slate-800 px-2 py-1 text-xs text-slate-200 hover:bg-slate-700">
            <Grid3X3 className="w-3 h-3" /> Auto hex-grid
          </button>
          <button onClick={clearAll} className="inline-flex items-center gap-1 rounded-md border border-slate-700 bg-slate-800 px-2 py-1 text-xs text-slate-200 hover:bg-slate-700">
            <RotateCcw className="w-3 h-3" /> Clear
          </button>
          <button onClick={exportJSON} className="inline-flex items-center gap-1 rounded-md border border-sky-700 bg-sky-900/40 px-2 py-1 text-xs text-sky-200 hover:bg-sky-900/70">
            <Save className="w-3 h-3" /> Export JSON
          </button>
        </div>
      </div>

      {/* Map */}
      <div className="relative inline-block rounded-lg overflow-hidden border border-slate-700">
        <canvas
          ref={canvasRef}
          onClick={handleClick}
          onContextMenu={handleContextMenu}
          onMouseMove={handleMouseMove}
          onMouseLeave={() => { setHoverDepth(null); setHoverPos(null); }}
          className="cursor-crosshair"
        />
        {hoverDepth !== null && hoverPos && (
          <div className="pointer-events-none fixed z-50 rounded-md bg-slate-900/95 border border-slate-700 px-2 py-1 text-xs text-slate-100 shadow-xl"
            style={{ left: hoverPos.x + 12, top: hoverPos.y + 12 }}>
            depth = <strong className="text-sky-300">{hoverDepth.toFixed(1)} m</strong>
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-3 text-xs text-slate-400 flex-wrap">
        <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded" style={{ background: 'rgb(140,210,250)' }} /> shallow</span>
        <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded" style={{ background: 'rgb(40,80,120)' }} /> deep</span>
        <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded" style={{ background: 'rgba(255,215,0,0.55)' }} /> deepest 30 %</span>
        <span className="text-slate-500">·</span>
        <span>Extent: <strong className="text-slate-200">{(bathymetry.extent_m.width / 1000).toFixed(2)} × {(bathymetry.extent_m.height / 1000).toFixed(2)} km</strong></span>
        <span>·</span>
        <span>Mean depth: <strong className="text-slate-200">{meanD.toFixed(1)} m</strong></span>
        <span>·</span>
        <span>Max depth: <strong className="text-slate-200">{depthMax.toFixed(1)} m</strong></span>
        <span>·</span>
        <span>Modules: <strong className="text-emerald-300">{modules.length}</strong></span>
      </div>

      {/* Module table */}
      {modules.length > 0 && (
        <div className="rounded-lg border border-slate-700 bg-slate-950/50 p-2 text-xs overflow-x-auto">
          <table className="w-full text-left">
            <thead className="text-slate-500 border-b border-slate-700">
              <tr>
                <th className="py-1 pr-2">#</th>
                <th className="py-1 pr-2">x (m)</th>
                <th className="py-1 pr-2">y (m)</th>
                <th className="py-1 pr-2">depth (m)</th>
                <th className="py-1 pr-2">deep-zone?</th>
                <th className="py-1 pr-2"></th>
              </tr>
            </thead>
            <tbody>
              {modules.map((m, i) => {
                const inDeep = m.depth >= raster.deepThreshold;
                return (
                  <tr key={m.id} className="border-b border-slate-800/60">
                    <td className="py-0.5 text-slate-300">{i + 1}</td>
                    <td className="py-0.5 text-slate-300 font-mono">{(m.x * bathymetry.extent_m.width).toFixed(0)}</td>
                    <td className="py-0.5 text-slate-300 font-mono">{(m.y * bathymetry.extent_m.height).toFixed(0)}</td>
                    <td className="py-0.5 text-sky-300 font-mono">{m.depth.toFixed(2)}</td>
                    <td className="py-0.5">
                      {inDeep ? <span className="text-emerald-300">✓ deep</span> : <span className="text-slate-500">shallow</span>}
                    </td>
                    <td className="py-0.5">
                      <button onClick={() => setModules(prev => prev.filter(p => p.id !== m.id))}
                        className="text-red-400 hover:text-red-300">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export const _placedModuleIcon = Plus;     // re-export to keep tree-shaker happy
